"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"

import { analytics } from "@/lib/analytics/client"

/**
 * Mede a saúde do carregamento de cada rota.
 *
 * Existe porque "a página recarrega sozinha" só chegava até nós por relato de
 * aluno, e relato não distingue as duas causas — que exigem correções opostas:
 *
 * - **recarregou**: o documento foi buscado de novo. Perde-se posição de vídeo
 *   e de rolagem. Causa fora do React (redirect em loop, crash da aba, F5).
 * - **remontou**: o documento é o mesmo e a árvore React caiu e voltou. Causa
 *   dentro do React (`key` instável, componente definido em render, Suspense
 *   reentrando).
 *
 * Para quem está olhando a tela os dois são "piscou e voltou". Aqui são dois
 * eventos, e é a diferença entre procurar no lugar certo e no errado.
 *
 * Não mede se ninguém reclamou: um pico de `page_reloaded` numa rota é o
 * próprio alarme.
 */

/** Dois carregamentos mais perto que isto não são o usuário apertando F5. */
const LOOP_WINDOW_MS = 10_000
const STORAGE_KEY = "page-health:last-load"

interface LastLoad {
  path: string
  at: number
}

function readLastLoad(): LastLoad | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as LastLoad).path !== "string" ||
      typeof (parsed as LastLoad).at !== "number"
    ) {
      return null
    }
    return parsed as LastLoad
  } catch {
    // sessionStorage bloqueado (Safari em navegação privada) não pode derrubar
    // a página: sem histórico, o beacon só perde o `since_previous_ms`.
    return null
  }
}

function writeLastLoad(value: LastLoad): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    /* idem */
  }
}

function navigationEntry(): PerformanceNavigationTiming | null {
  const [entry] = performance.getEntriesByType(
    "navigation"
  ) as PerformanceNavigationTiming[]
  return entry ?? null
}

/**
 * Montagens por rota nesta aba. Vive fora do componente de propósito: o ponto
 * é justamente sobreviver à remontagem que estamos medindo — num `useRef` o
 * contador morreria junto com o que ele conta.
 */
const mountsByPath = new Map<string, { count: number; at: number }>()

/**
 * Conta montagens de um pedaço da árvore e acusa a segunda em diante.
 *
 * Um `scope` por subárvore que valha a pena vigiar. A raiz não basta: quando
 * só a tela da aula remonta, o provedor no layout não desmonta e não teria o
 * que reportar — foi exatamente o buraco que este parâmetro fecha.
 */
export function useRemountProbe(scope: string): void {
  const pathname = usePathname()
  // Guarda contra o duplo-invoke do StrictMode, que remontaria de mentira.
  const reportedRef = useRef<string | null>(null)

  const key = `${scope}:${pathname}`

  useEffect(() => {
    if (reportedRef.current === key) return
    reportedRef.current = key

    const now = performance.timeOrigin + performance.now()
    const previous = mountsByPath.get(key)
    const mountCount = (previous?.count ?? 0) + 1
    mountsByPath.set(key, { count: mountCount, at: now })

    // Segunda montagem da MESMA rota sem o documento ter sido buscado de novo:
    // a árvore caiu por baixo.
    if (previous) {
      analytics.capture("page_remounted", {
        path: pathname,
        scope,
        mount_count: mountCount,
        since_previous_ms: Math.round(now - previous.at),
      })
    }
  }, [key, pathname, scope])
}

/**
 * Rotas cuja carga já foi medida nesta aba.
 *
 * Fora do componente pelo mesmo motivo do contador: carregar o documento é um
 * fato da ABA, não da montagem. Se o registro morresse junto com o componente,
 * cada remontagem remediria a mesma carga e o número viraria ruído — que é o
 * oposto de uma linha de base.
 */
const loadReported = new Set<string>()

export function PageHealthBeacon() {
  const pathname = usePathname()
  useRemountProbe("app")
  const reportedRef = useRef<string | null>(null)

  useEffect(() => {
    if (reportedRef.current === pathname) return
    reportedRef.current = pathname
    if (loadReported.has(pathname)) return
    loadReported.add(pathname)

    const now = performance.timeOrigin + performance.now()
    const entry = navigationEntry()
    const navigationType = entry?.type ?? "unknown"

    // Lido ANTES de escrever, e escrito em TODO carregamento — não só nos
    // recarregamentos. Só assim o primeiro reload já sai com o intervalo: se
    // o carimbo nascesse no reload, o evento mais informativo de todos (o
    // primeiro) seria justamente o que sai sem intervalo.
    const last = readLastLoad()
    const sincePrevious =
      last && last.path === pathname ? Math.round(now - last.at) : undefined
    writeLastLoad({ path: pathname, at: now })

    if (entry) {
      analytics.capture("page_ready", {
        path: pathname,
        navigation_type: navigationType,
        load_ms: Math.round(entry.duration),
      })
    }

    if (navigationType !== "reload") return

    analytics.capture("page_reloaded", {
      path: pathname,
      since_previous_ms: sincePrevious,
      looks_like_loop:
        sincePrevious !== undefined && sincePrevious < LOOP_WINDOW_MS,
    })
  }, [pathname])

  return null
}
